import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Truck,
  FileText,
  BarChart3,
  BellRing,
  PackageCheck,
  Quote,
} from "lucide-react";
import { Reveal, MaskedLine, Eyebrow } from "@/components/site/Reveal";
import Lightbox from "@/components/site/Lightbox";
import Marquee from "@/components/site/Marquee";
import DashboardMockup from "@/components/site/DashboardMockup";

const CHAPTERS = [
  {
    num: "01",
    title: "The MSME grind is real",
    text: "Chasing payments on chat, quotes lost in notebooks, trucks nobody can locate. Small businesses don't lack ambition — they lack hours in the day.",
  },
  {
    num: "02",
    title: "AI that is practical, not scary",
    text: "YesAI builds simple custom AI apps that plug into how you already work. No jargon, no consultants, no six-month rollouts — tools your team learns in a day.",
  },
  {
    num: "03",
    title: "Built to move your numbers",
    text: "More visits logged, more quotes sent, more deals won, every dispatch accounted for. That is the only scoreboard we care about.",
  },
];

const INDUSTRIES = [
  { icon: Truck, name: "RMC & Construction", note: "Dispatch and field sales, tracked live" },
  { icon: PackageCheck, name: "Retail & Wholesale", note: "Stock alerts and reorder reminders" },
  { icon: FileText, name: "Logistics & Fleet", note: "Trip sheets and delivery proof, automated" },
  { icon: BellRing, name: "Field Services", note: "Follow-ups that never fall through" },
  { icon: BarChart3, name: "Manufacturing", note: "Daily reports without the end-of-day slog" },
];

const TESTIMONIALS = [
  {
    quote: "Our dispatch confusion ended in a week. Drivers, sales team, office — everyone finally sees the same picture.",
    name: "Ravi Menon",
    role: "Owner, Metro RMC",
    initials: "RM",
  },
  {
    quote: "Quotes that took two days now go out in an hour. YesAI understood our business before writing a line of code.",
    name: "Sana Qureshi",
    role: "Director, Qureshi Builders Supply",
    initials: "SQ",
  },
  {
    quote: "I run my whole sales team from my phone. Simple, fast, and it just works — that's all I ever wanted.",
    name: "Deepak Patil",
    role: "Founder, Patil Constructions",
    initials: "DP",
  },
];

export default function Home() {
  const [lightbox, setLightbox] = useState(null);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative pt-14 sm:pt-20 pb-20 sm:pb-24" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-10 items-center">
          <div>
            <Reveal y={16}>
              <Eyebrow>AI-Powered Business Apps</Eyebrow>
            </Reveal>
            <h1 className="mt-5 font-display font-black tracking-tight leading-[1.04] text-5xl sm:text-6xl lg:text-[4.4rem]" data-testid="hero-headline">
              <MaskedLine delay={0.15}>Work Smarter.</MaskedLine>
              <MaskedLine delay={0.32}>
                <span className="text-brand-red">Grow Better.</span>
              </MaskedLine>
            </h1>
            <Reveal delay={0.5}>
              <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-md" data-testid="hero-subtext">
                YesAI builds simple, practical apps that solve everyday operational challenges for small businesses — from field sales to dispatch and beyond.
              </p>
            </Reveal>
            <Reveal delay={0.65}>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to="/book-demo"
                  data-testid="hero-book-demo-btn"
                  className="group inline-flex items-center gap-2.5 bg-brand-red hover:bg-brand-redDark text-white font-bold px-7 py-3.5 rounded-full transition-colors duration-200 shadow-xl shadow-brand-red/25"
                >
                  Book a Demo
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
                </Link>
                <Link
                  to="/products"
                  data-testid="hero-explore-products-btn"
                  className="inline-flex items-center gap-2.5 border-2 border-brand-red text-brand-red hover:bg-brand-red hover:text-white font-bold px-7 py-3.5 rounded-full transition-colors duration-200"
                >
                  Explore Products
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.8}>
              <div className="mt-12 grid grid-cols-3 gap-6 max-w-md" data-testid="hero-benefits-row">
                {[
                  { num: "01", label: "Simple to use" },
                  { num: "02", label: "Built for business" },
                  { num: "03", label: "Mobile friendly" },
                ].map((b) => (
                  <div key={b.num}>
                    <p className="font-display font-extrabold text-brand-red text-lg">{b.num}</p>
                    <p className="mt-1 text-sm text-slate-500">{b.label}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.45} y={40}>
            <DashboardMockup />
          </Reveal>
        </div>
      </section>

      <Marquee />

      {/* MANIFESTO */}
      <section className="py-24 sm:py-32" data-testid="manifesto-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Eyebrow>The YesAI Manifesto</Eyebrow>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-2xl leading-tight">
              Three beliefs behind every app we ship.
            </h2>
          </Reveal>
          <div className="mt-16 grid md:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-3xl overflow-hidden">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.12} className="bg-white">
                <div className="p-8 sm:p-10 h-full hover:bg-brand-redSoft/40 transition-colors duration-300" data-testid={`manifesto-chapter-${c.num}`}>
                  <p className="font-mono text-brand-red font-semibold text-sm tracking-widest">{c.num}</p>
                  <h3 className="mt-5 font-display font-bold text-xl sm:text-2xl tracking-tight">{c.title}</h3>
                  <p className="mt-4 text-slate-600 text-sm leading-relaxed">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FLAGSHIP SPOTLIGHT */}
      <section className="bg-brand-ink text-white py-24 sm:py-32 relative overflow-hidden" data-testid="flagship-spotlight">
        <div className="absolute -left-32 bottom-0 w-[420px] h-[420px] rounded-full bg-brand-red/20 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full rounded-3xl border-2 border-brand-red/60 pointer-events-none" />
              <button
                onClick={() => setLightbox({ src: "/saleyes-promo.jpg", alt: "SaleYes app poster" })}
                className="block w-full cursor-zoom-in rounded-3xl"
                data-testid="flagship-poster-trigger"
                aria-label="View SaleYes poster full screen"
              >
                <img
                  src="/saleyes-promo.jpg"
                  alt="SaleYes app — manage customers, track leads, plan visits, monitor sales and achieve targets"
                  className="rounded-3xl w-full h-auto shadow-2xl"
                  loading="lazy"
                  data-testid="flagship-image"
                />
              </button>
              <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur rounded-2xl px-4 py-3 shadow-lg">
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Dispatches today</p>
                <p className="font-display font-extrabold text-xl text-brand-ink">890 <span className="text-emerald-600 text-sm font-bold">+31.2%</span></p>
              </div>
            </div>
          </Reveal>
          <div>
            <Reveal>
              <Eyebrow dark>Flagship Product</Eyebrow>
              <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight">
                Meet <span className="inline-flex items-center bg-white rounded-full px-4 py-1.5 align-middle mx-1 shadow-lg"><img src="/saleyes-logo.png" alt="SaleYes — Sales Buddy" className="h-8 sm:h-10 w-auto" /></span> — lead to sale, with in-app reminders so no lead is ever missed.
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 text-slate-300 leading-relaxed max-w-lg">
                Lead sales made simple: every lead tracked stage by stage, with in-app reminders so follow-ups never slip — all from the phone already in your pocket.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <ul className="mt-8 space-y-4">
                {[
                  "In-app reminders so no follow-up is ever missed",
                  "Every lead tracked stage by stage, enquiry to deal won",
                  "Daily sales summary, delivered automatically",
                ].map((point) => (
                  <li key={point} className="flex items-start gap-3 text-sm text-slate-200">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-brand-red flex items-center justify-center shrink-0">
                      <ArrowRight className="w-3 h-3 text-white" />
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.35}>
              <Link
                to="/products"
                data-testid="spotlight-explore-saleyes-btn"
                className="mt-10 inline-flex items-center gap-2.5 bg-white text-brand-ink hover:bg-brand-red hover:text-white font-bold px-7 py-3.5 rounded-full transition-colors duration-200"
              >
                Explore SaleYes
                <ArrowUpRight className="w-5 h-5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* INDUSTRIES SNAPSHOT */}
      <section className="py-24 sm:py-32 bg-slate-50" data-testid="industries-snapshot">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
              <div>
                <Eyebrow>Industries</Eyebrow>
                <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-xl">
                  Everyday AI for every kind of small business.
                </h2>
              </div>
              <Link
                to="/industries"
                data-testid="snapshot-industries-link"
                className="group inline-flex items-center gap-2 font-bold text-brand-red text-sm shrink-0"
              >
                See all industries
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
            </div>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.name} delay={i * 0.08}>
                <div
                  data-testid={`industry-card-${i}`}
                  className="group bg-white border border-slate-200/80 rounded-2xl p-6 h-full hover:shadow-xl hover:-translate-y-1 hover:border-brand-red/30 transition-shadow transition-transform duration-300"
                >
                  <span className="w-11 h-11 rounded-xl bg-brand-redSoft text-brand-red flex items-center justify-center group-hover:bg-brand-red group-hover:text-white transition-colors duration-300">
                    <ind.icon className="w-5 h-5" />
                  </span>
                  <h3 className="mt-5 font-display font-bold text-base tracking-tight">{ind.name}</h3>
                  <p className="mt-2 text-xs text-slate-500 leading-relaxed">{ind.note}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 sm:py-32" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Word on the street</Eyebrow>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-2xl">
              Owners who got their evenings back.
            </h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.1}>
                <figure
                  data-testid={`testimonial-${i}`}
                  className="bg-white border border-slate-200/80 rounded-3xl p-8 h-full flex flex-col hover:shadow-xl hover:-translate-y-1 transition-shadow transition-transform duration-300"
                >
                  <Quote className="w-7 h-7 text-brand-red" />
                  <blockquote className="mt-5 text-slate-600 text-sm leading-relaxed flex-1">"{t.quote}"</blockquote>
                  <figcaption className="mt-7 flex items-center gap-3">
                    <span className="w-10 h-10 rounded-full bg-brand-ink text-white font-display font-bold text-xs flex items-center justify-center">
                      {t.initials}
                    </span>
                    <div>
                      <p className="font-bold text-sm">{t.name}</p>
                      <p className="text-xs text-slate-500">{t.role}</p>
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="pb-24 sm:pb-32 px-5 sm:px-8" data-testid="home-cta-banner">
        <Reveal>
          <div className="max-w-7xl mx-auto bg-brand-red rounded-[2.5rem] px-8 sm:px-14 py-16 sm:py-20 relative overflow-hidden">
            <div aria-hidden className="absolute -right-16 -top-24 font-display font-black text-[11rem] leading-none text-white/10 select-none pointer-events-none">
              GO
            </div>
            <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              <div>
                <h2 className="font-display font-extrabold text-white tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight">
                  Ready to work smarter?
                </h2>
                <p className="mt-4 text-white/80 max-w-md text-sm sm:text-base leading-relaxed">
                  Book a free demo. We'll walk you through YesAI on your own business numbers — 30 minutes, no obligation.
                </p>
              </div>
              <Link
                to="/book-demo"
                data-testid="cta-book-demo-btn"
                className="group inline-flex items-center justify-center gap-2.5 bg-white text-brand-red font-bold px-8 py-4 rounded-full hover:bg-brand-ink hover:text-white transition-colors duration-200 shrink-0"
              >
                Book a Demo
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      <Lightbox src={lightbox?.src} alt={lightbox?.alt || ""} onClose={() => setLightbox(null)} />
    </div>
  );
}
